import { useEffect, useMemo, useRef, useState } from 'react';

type GeoPermission = PermissionState | 'unsupported' | null;

export type PositionState = {
  latitude: number | null;
  longitude: number | null;
  accuracy: number | null;
  altitude: number | null;
  heading: number | null;
  speed: number | null;
  timestamp: number | null;
  error: string | null;
  isLoading: boolean;
  isSupported: boolean;
  permission: GeoPermission;
};

type Options = PositionOptions & {
  watch?: boolean; // default true
};

export default function usePosition(opts: Options = {}) {
  const { watch = true, enableHighAccuracy, timeout, maximumAge } = opts;
  const options = useMemo<PositionOptions>(() => ({ enableHighAccuracy, timeout, maximumAge }), [enableHighAccuracy, timeout, maximumAge]);

  const [state, setState] = useState<PositionState>({
    latitude: null,
    longitude: null,
    accuracy: null,
    altitude: null,
    heading: null,
    speed: null,
    timestamp: null,
    error: null,
    isLoading: true,
    isSupported: typeof navigator !== 'undefined' && 'geolocation' in navigator,
    permission: null,
  });

  const watchId = useRef<number | null>(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    if (!state.isSupported) {
      setState(s => ({ ...s, isLoading: false, permission: 'unsupported', error: 'Geolocation not supported' }));
      return;
    }

    // permissions API (optional)
    if ('permissions' in navigator && (navigator as any).permissions?.query) {
      (navigator as any).permissions.query({ name: 'geolocation' as PermissionName })
        .then((res: PermissionStatus) => {
          if (!mounted.current) return;
          setState(s => ({ ...s, permission: res.state }));
          res.onchange = () => mounted.current && setState(s => ({ ...s, permission: res.state }));
        })
        .catch(() => {/* ignore */});
    }

    const onSuccess = (pos: GeolocationPosition) => {
      if (!mounted.current) return;
      const { latitude, longitude, accuracy, altitude, heading, speed } = pos.coords;
      setState({
        latitude, longitude, accuracy: accuracy ?? null, altitude: altitude ?? null,
        heading: heading ?? null, speed: speed ?? null, timestamp: pos.timestamp,
        error: null, isLoading: false, isSupported: true, permission: state.permission,
      });
    };

    const onError = (err: GeolocationPositionError) => {
      if (!mounted.current) return;
      setState(s => ({ ...s, error: err.message, isLoading: false }));
    };

    if (watch) {
      watchId.current = navigator.geolocation.watchPosition(onSuccess, onError, options);
      return () => {
        if (watchId.current !== null) navigator.geolocation.clearWatch(watchId.current);
      };
    } else {
      navigator.geolocation.getCurrentPosition(onSuccess, onError, options);
      return () => {};
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch, options]);

  return state;
}
